import type { SystemHealthActivity } from '../../../types/systemHealth'

interface ActivitySectionProps {
  activity: SystemHealthActivity | undefined
  isLoading: boolean
}

const formatTime = (epochSeconds: number): string => {
  const d = new Date(epochSeconds * 1000)
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
}

const formatBucketTime = (iso: string): string => {
  const d = new Date(iso)
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })
}

export function ActivitySection({ activity, isLoading }: ActivitySectionProps) {
  const maxBucketCount = activity?.buckets.reduce((m, b) => Math.max(m, b.messages), 0) || 1

  return (
    <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent activity (last hour)</h2>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !activity ? (
        <p className="text-sm text-gray-500">No data.</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-6 mb-6">
            <Stat label="Total messages" value={activity.total} />
            <Stat label="Active devices" value={activity.devices} />
            <Stat label="Avg rate" value={`${activity.perMin} msg/min`} />
          </div>

          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              {activity.bucketMinutes}-minute buckets
            </h3>
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
                <tr>
                  <th className="py-2 pr-4">Time</th>
                  <th className="py-2 pr-4 text-right">Messages</th>
                  <th className="py-2 pr-4 text-right">Devices</th>
                  <th className="py-2 w-1/2">Volume</th>
                </tr>
              </thead>
              <tbody>
                {activity.buckets.map((b) => (
                  <tr key={b.bucketStart} className="border-b border-gray-100 last:border-0">
                    <td className="py-2 pr-4 font-mono text-gray-700">{formatBucketTime(b.bucketStart)}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">{b.messages}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">{b.devices}</td>
                    <td className="py-2">
                      <div className="h-3 bg-gray-100 rounded">
                        <div
                          className="h-3 bg-blue-500 rounded"
                          style={{ width: `${(b.messages / maxBucketCount) * 100}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
                {activity.buckets.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-3 text-center text-gray-500">
                      No messages in window.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Last messages</h3>
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
                  <tr>
                    <th className="py-2 pr-4">Time</th>
                    <th className="py-2 pr-4">Device</th>
                    <th className="py-2 pr-4 text-right">Seq</th>
                    <th className="py-2 pr-4">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.lastMessages.map((m, i) => (
                    <tr key={i} className="border-b border-gray-100 last:border-0">
                      <td className="py-2 pr-4 font-mono text-gray-700">{formatTime(m.sigfoxTime)}</td>
                      <td className="py-2 pr-4 font-mono text-gray-700">{m.sigfoxId}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{m.sigfoxSnum}</td>
                      <td className="py-2 pr-4">{m.msgType}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Top devices</h3>
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
                  <tr>
                    <th className="py-2 pr-4">Device</th>
                    <th className="py-2 pr-4 text-right">Count</th>
                    <th className="py-2 pr-4">Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.topDevices.map((d) => (
                    <tr key={d.sigfoxId} className="border-b border-gray-100 last:border-0">
                      <td className="py-2 pr-4 font-mono text-gray-700">{d.sigfoxId}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{d.msgCount}</td>
                      <td className="py-2 pr-4 font-mono text-gray-700">{formatTime(d.lastSeen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </section>
  )
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-2xl font-semibold text-gray-900 tabular-nums">{value}</p>
    </div>
  )
}
